/**
 * У каких площадок есть знак в `public/venues`.
 *
 * Список считается заранее, а не проверяется в браузере. Проверять пришлось бы
 * запросом: показать картинку, поймать `onerror`, убрать её и нарисовать
 * словесную марку — то есть моргнуть пустотой на каждой незнакомой конторе.
 * Зеркальная лента приносит конторы, которых мы не знаем, поэтому моргало бы
 * постоянно.
 *
 * Список `HAVE` переписывает `scripts/venue-logos/normalize.py` вместе со
 * знаками: два места, которые обязаны совпадать, не должны редактироваться
 * руками по отдельности. Таблица синонимов ниже — ручная и скриптом не
 * трогается: имя файла знает скрипт, а как контору зовёт источник — знаем мы.
 */

import { FEED_KIND } from '../lib/feedContext';

const HAVE = new Set([
  '1win',
  '1xbet',
  '1xstavka',
  '22bet',
  '22bet_ng',
  '32red',
  '5051x',
  '888sport',
  '888starz',
  'altenar',
  'azuro',
  'bcgame',
  'bet365',
  'betboom',
  'betby',
  'betcity',
  'betcity_nl',
  'betfair',
  'betfury',
  'betinasia',
  'betwinner',
  'bingo',
  'blaze',
  'bovada',
  'buff',
  'cbet',
  'cdna',
  'cloudbet',
  'csgopositive',
  'dafabet',
  'dexsport',
  'duel',
  'duelbits',
  'egb',
  'fonbet',
  'fortunejack',
  'ganaexpress',
  'ggbet',
  'goldenpalace',
  'igame',
  'jetton',
  'kalshi',
  'kambi',
  'leon',
  'ligastavok',
  'limitless',
  'lsbet',
  'marathon',
  'mariacasino',
  'matchbook',
  'megapari',
  'melbet',
  'mostbet',
  'myriad',
  'nairabet',
  'novig',
  'olimp',
  'onestepcasino',
  'onexbet',
  'onexbetru',
  'opinion',
  'overtime',
  'paf',
  'palmsbet',
  'pari',
  'parimatch',
  'pinnacle',
  'pinup',
  'polkamarkets',
  'poly',
  'polymarket',
  'predictfun',
  'predictit',
  'rainbet',
  'riobet',
  'rivalry',
  'rollbit',
  'roobet',
  'shuffle',
  'smarkets',
  'sportsbet',
  'stake',
  'storspelare',
  'sharpxch',
  'sxbet',
  'thunderpick',
  'unibet',
  'unibet_co_uk',
  'unibet_nl',
  'vbet',
  'vbet_am',
  'vivarobet',
  'welstroy',
  'winline',
  'xlivebet',
  'zenit',
]);

/**
 * Одна контора приходит разными кодами, и знак у неё один.
 *
 * Слева — как контору называет источник, справа — имя файла. Каждая строка
 * оплачена пустым местом в ленте: `onexstavka`, `pm` и `bingoboom` занимают
 * пятую часть всех ног зеркала, и знака у них не было ни у одной.
 *
 * Это НЕ склейка площадок для матчинга — та живёт в `matching/clones.py` и
 * решает, считать ли две ноги одной конторой. Здесь решается только «чью
 * картинку показать», и цена ошибки — чужой значок, а не фантомная вилка.
 */
const ALIAS: Record<string, string> = {
  // 1xСтавка, Melbet и 22bet — одна линия. В ленте это одна кнопка «1xБит».
  onexstavka: 'onexbet',
  '1xstavka': 'onexbet',
  '1xbit': 'onexbet',
  melbet: 'onexbet',
  '22bet': 'onexbet',
  '22bet_ng': 'onexbet',
  pm: 'parimatch',
  gg: 'ggbet',
  dafa: 'dafabet',
  betxyz: 'bxyz',
  // Контора переименовалась: `bingoboom.ru` отвечает 301 на `betboom.ru`, и
  // знак у неё сегодня один — новый.
  bingoboom: 'betboom',
  marathonbet: 'marathon',
  pinnaclesports: 'pinnacle',
  // Вилка приходит как Betfair; человек ставит на Orbitex.
  betfair: 'orbitex',
  betfairsb: 'orbitex',
  unibet_co_uk: 'unibet',
  unibet_nl: 'unibet',
  sportsbetio: 'sportsbet',
  pinupbet: 'pinup',
  bookmakerxyz: 'azuro',
  cryptocom: 'cdna',
  // Вывеска Mellstroy, файл и код источника — `welstroy`.
  mellstroy: 'welstroy',
  // Короткий код зеркала: иначе в сите две кнопки — `gg` и `ggbet`.
  poly: 'polymarket',
  onewin: 'welstroy',
  '1win': 'welstroy',
  // Зеркало и адаптер зовут `buffbet`, файл знака — `buff` (домен buff.bet).
  buffbet: 'buff',
  // Вывеска в сите «SharpPCH» — тот же Sharp Exchange (`sharpxch`).
  sharppch: 'sharpxch',
  dexport: 'dexsport',
  // The mirror's FortuneJack clone is shown under the JetTon account.
  fortunejack: 'jetton',
};

/**
 * Код источника к имени файла.
 *
 * Пишут по-разному: `Bet365`, `BET_365`, `bc.game`. Мост канала A ещё и
 * приставляет к бренду свою метку — `gg~pm`: берём то, что после тильды, иначе
 * `gg~pm` слился бы с кодом `ggpm`, которого нет ни у кого.
 */
const key = (code: string): string => {
  const brand = code.slice(code.lastIndexOf('~') + 1);
  const bare = brand.toLowerCase().replace(/[^a-z0-9_]/g, '');
  if ((FEED_KIND === 'main' || FEED_KIND === 'tests') && (bare === '1win' || bare === 'onewin')) return '1win';
  return ALIAS[bare] ?? bare;
};

/** Канон кассы: `GG` и `ggbet` — одна кнопка, один знак. */
export function venueCanon(code: string): string {
  return key(code);
}

/**
 * Хвост памяти сита: в ГГ за неделю и в своих адаптерах нет, Вилка их не везла.
 * Знаки и каталог не удаляем — только кнопки. 1.10, слово владельца.
 */
import { RU_FEED, PRIVATE_MIRROR } from '../lib/feedContext';

const SIEVE_DROP_BOOKS = new Set(['kalshi', 'stake', 'dexsport', 'sharpxch', 'buff']);

/** Контора, которой в сите /app больше не быть. */
export function sieveDropsBook(code: string): boolean {
  const canon = venueCanon(code);
  if ((RU_FEED || PRIVATE_MIRROR) && canon === 'kalshi') return false;
  return SIEVE_DROP_BOOKS.has(canon) || SIEVE_DROP_BOOKS.has(code.trim().toLowerCase());
}

/** Адрес знака площадки или `null`, если знака нет и рисовать надо словом. */
export function logoOf(code: string): string | null {
  const name = venueCanon(code);
  const file = (name === 'onexbet' || name === 'onexbetru') ? '1xbet' : name === 'orbitex' ? 'betfair' : name;
  return HAVE.has(file) ? `${import.meta.env.BASE_URL}venues/${file}.webp` : null;
}
